import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { adminInvitationService } from '../../services/api';

// Async thunks
export const fetchInvitations = createAsyncThunk(
  'invitations/fetchInvitations',
  async (filters, { rejectWithValue }) => {
    try {
      console.log('Fetching invitations with filters:', filters);
      const response = await adminInvitationService.getInvitations(filters);
      console.log('Invitations fetched successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching invitations:', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch invitations');
    }
  }
);

export const createInvitation = createAsyncThunk(
  'invitations/createInvitation',
  async (invitationData, { rejectWithValue }) => {
    try {
      console.log('Creating invitation with data:', invitationData);
      const response = await adminInvitationService.createInvitation(invitationData);
      console.log('Invitation created successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error creating invitation:', error);
      // Properly format the error message
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          'Failed to create invitation';
      return rejectWithValue(errorMessage);
    }
  }
);

export const deleteInvitation = createAsyncThunk(
  'invitations/deleteInvitation',
  async (id, { rejectWithValue }) => {
    try {
      await adminInvitationService.deleteInvitation(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete invitation');
    }
  }
);

export const resendInvitation = createAsyncThunk(
  'invitations/resendInvitation',
  async (id, { rejectWithValue }) => {
    try {
      const response = await adminInvitationService.resendInvitation(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to resend invitation');
    }
  }
);

// Slice
const invitationSlice = createSlice({
  name: 'invitations',
  initialState: {
    items: [],
    loading: false,
    error: null,
    filters: {
      status: 'all',
      page: 1,
      limit: 10
    },
    stats: {
      total: 0,
      pending: 0,
      used: 0,
      expired: 0
    },
    selectedInvitation: null
  },
  reducers: {
    setInvitationFilters: (state, action) => {
      // Log the filter updates for debugging
      console.log('Setting invitation filters:', action.payload);
      
      // Clean up any temporary _refresh parameter when storing in state
      const { _refresh, ...permanentFilters } = action.payload;
      state.filters = permanentFilters;
    },
    clearInvitationError: (state) => {
      state.error = null;
    },
    resetInvitationState: (state) => {
      state.loading = false;
      state.error = null;
    },
    setSelectedInvitation: (state, action) => {
      state.selectedInvitation = action.payload;
    },
    clearSelectedInvitation: (state) => {
      state.selectedInvitation = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // fetchInvitations
      .addCase(fetchInvitations.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchInvitations.fulfilled, (state, action) => {
        state.loading = false;
        console.log('Invitations fetched successfully. Total:', action.payload.total);
        console.log('Invitations data:', action.payload.invitations);
        
        // Ensure we have an array of invitations, even if the backend returns null/undefined
        const rawInvitations = Array.isArray(action.payload.invitations) 
          ? action.payload.invitations 
          : [];
        
        // Transform the snake_case API response to camelCase for frontend consistency
        const invitations = rawInvitations.map(inv => ({
          id: inv.id,
          email: inv.email,
          token: inv.token,
          status: inv.status,
          createdAt: inv.CreatedAt,
          updatedAt: inv.UpdatedAt, 
          expiresAt: inv.expires_at,
          createdBy: inv.created_by,
          usedAt: inv.used_at,
          notes: inv.notes,
          // Keep original fields too for backward compatibility
          ...inv
        }));
        
        console.log('Transformed invitations:', invitations);
          
        state.items = invitations;
        state.stats = {
          total: action.payload.total || 0,
          pending: invitations.filter(i => i.status === 'pending').length,
          used: invitations.filter(i => i.status === 'used').length,
          expired: invitations.filter(i => i.status === 'expired').length
        };
      })
      .addCase(fetchInvitations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // createInvitation
      .addCase(createInvitation.pending, (state) => {
        state.loading = true;
        state.error = null; // Clear any previous errors
      })
      .addCase(createInvitation.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        
        // Transform the API response to match our frontend format
        const newInvitation = {
          id: action.payload.id,
          email: action.payload.email,
          token: action.payload.token,
          status: action.payload.status,
          createdAt: action.payload.CreatedAt || new Date().toISOString(),
          updatedAt: action.payload.UpdatedAt || new Date().toISOString(),
          expiresAt: action.payload.expires_at,
          createdBy: action.payload.created_by,
          usedAt: action.payload.used_at,
          notes: action.payload.notes,
          // Keep original fields too for backward compatibility
          ...action.payload
        };
        
        console.log('Transformed new invitation:', newInvitation);
        state.items.unshift(newInvitation);
        state.stats.total++;
        state.stats.pending++;
      })
      .addCase(createInvitation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        console.log('Invitation creation rejected with error:', action.payload);
      })
      
      // deleteInvitation
      .addCase(deleteInvitation.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteInvitation.fulfilled, (state, action) => {
        state.loading = false;
        const deletedItem = state.items.find(item => item.id === action.payload);
        if (deletedItem) {
          state.items = state.items.filter(item => item.id !== action.payload);
          state.stats.total--;
          if (deletedItem.status === 'pending') state.stats.pending--;
          if (deletedItem.status === 'used') state.stats.used--;
          if (deletedItem.status === 'expired') state.stats.expired--;
        }
      })
      .addCase(deleteInvitation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // resendInvitation
      .addCase(resendInvitation.pending, (state) => {
        state.loading = true;
      })
      .addCase(resendInvitation.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.items.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
      })
      .addCase(resendInvitation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { 
  setInvitationFilters, 
  clearInvitationError,
  resetInvitationState, 
  setSelectedInvitation, 
  clearSelectedInvitation 
} = invitationSlice.actions;

export default invitationSlice.reducer;
